import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { Button, H1, H3, Input } from 'ui-rollup';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from './index.module.css';
import { Dispatch, SetStateAction, useState } from 'react';
import { fetchData } from '../utils/fetchData';
import { SubmitHandler, useForm } from 'react-hook-form';

enum ACTION {
  LOGIN,
  REGSITER,
}

export const Index = () => {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState(ACTION.LOGIN);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
      }}
    >
      <header className={styles.header}>
        <H1>Welcome to Invoicer</H1>
        <H3>Let's make invoice un-boring🥳</H3>
      </header>
      <main className={styles.main}>
        <Button
          onClick={() => {
            setOpen((open) => !open);
            setAction(ACTION.LOGIN);
          }}
        >
          Login
        </Button>
        <Button
          onClick={() => {
            setOpen((open) => !open);
            setAction(ACTION.REGSITER);
          }}
        >
          Register
        </Button>
      </main>
      {action === ACTION.LOGIN && <Login open={open} onOpenChange={setOpen} />}
      {action === ACTION.REGSITER && (
        <RegisterUser open={open} onOpenChange={setOpen} />
      )}
    </div>
  );
};

const Login = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}) => {
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const schema = z.object({
    email: z.string().email(),
    password: z.string().min(5),
  });
  type Login = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Login>({
    resolver: zodResolver(schema),
  });

  const handleLogin: SubmitHandler<Login> = async (data) => {
    try {
      const response = await fetchData<{ token: string }>(
        `${process.env.REACT_APP_BACKEND_API}/auth/login`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
      );

      sessionStorage.setItem('token', response.token);
      navigate('dashboard');
    } catch (e) {
      const errorString = JSON.parse(e.message);
      console.error(errorString);
      setError(errorString.statusText);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.DialogOverlay} />
        <Dialog.Content className={styles.DialogContent}>
          <Dialog.Title className={styles.DialogTitle}>
            <H3>Login 🔐</H3>
          </Dialog.Title>
          <form
            className={styles.formContainer}
            onSubmit={handleSubmit(handleLogin)}
          >
            <Input
              label="Email"
              placeholder="Enter here"
              type="email"
              errorMessage={errors?.email?.message}
              {...register('email')}
            />
            <Input
              label="Password"
              placeholder="Enter here"
              type="password"
              errorMessage={errors?.password?.message}
              {...register('password')}
            />
            {error.length ? <p>{error}</p> : null}
            <Button type="submit">Login</Button>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const RegisterUser = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}) => {
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const schema = z.object({
    name: z.string().min(1),
    email: z.string().email(),
    password: z.string().min(5),
    phone: z.string().length(10),
    companyName: z.string().min(1),
    gst: z.string().length(15), // TODO: Add regex validation for GST
    address: z.string().min(5).max(100),
  });
  type Register = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Register>({
    resolver: zodResolver(schema),
  });

  const handleRegister: SubmitHandler<Register> = async (data) => {
    try {
      const response = await fetchData<any>(
        `${process.env.REACT_APP_BACKEND_API}/users`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
      );

      //  TODO: JWT should be created instead of this
      if (response !== undefined) {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json; charset=utf-8');

        fetch(`${process.env.REACT_APP_BACKEND_API}/auth/login`, {
          headers,
          method: 'POST',
          body: JSON.stringify({
            email: data.email,
            password: data.password,
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            sessionStorage.setItem('token', data.token);
            navigate('dashboard');
          });
      }
      // onOpenChange(false);
    } catch (e) {
      console.error(e);
      setError(e.message.statusText);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.DialogOverlay} />
        <Dialog.Content className={styles.DialogContent}>
          <Dialog.Title className={styles.DialogTitle}>
            <H3>Register 🆕</H3>
          </Dialog.Title>
          <form
            className={styles.formContainer}
            onSubmit={handleSubmit(handleRegister)}
          >
            <Input
              label="Email"
              placeholder="Enter here"
              type="email"
              errorMessage={errors.email?.message}
              {...register('email')}
            />
            <Input
              label="Password"
              placeholder="Enter here"
              type="password"
              errorMessage={errors.password?.message}
              {...register('password')}
            />
            <Input
              label="Name"
              placeholder="Enter here"
              errorMessage={errors.name?.message}
              {...register('name')}
            />
            <Input
              label="Company"
              placeholder="Enter here"
              errorMessage={errors.companyName?.message}
              {...register('companyName')}
            />
            <Input
              label="Address"
              placeholder="Enter here"
              errorMessage={errors.address?.message}
              {...register('address')}
            />
            <Input
              label="Phone"
              placeholder="Enter here"
              type="tel"
              errorMessage={errors.phone?.message}
              {...register('phone')}
            />
            <Input
              label="GST"
              placeholder="Enter here"
              errorMessage={errors.gst?.message}
              {...register('gst')}
            />
            {error.length ? <p>{error}</p> : null}
            <Button type="submit">Submit</Button>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
